import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';

import themed from 'webapp/decorators/themed';
import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { GroupSelected } from 'webapp/components/form/checkbox-group';
import FormCheckboxSingleComponent from 'webapp/components/form/checkbox-single';
import ScrollableTextComponent from 'webapp/components/scrollable-text';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

export type SingleCheckboxValue = string | number;

export interface CheckboxItemInfo {
	value?: SingleCheckboxValue;
	label?: string;
	disabled?: boolean;
	tooltip?: string;
	tooltipIcon?: IconName;
	tooltipMaxWidth?: number;
	tooltipTheme?: string;
	used?: number | string;
	usedTooltip?: string;
	link?: {
		label: string;
		href?: string;
		target?: string;
		style?: SafeString;
		action: () => void;
	};
}

interface CheckboxArgsBase {
	name?: string;
	label?: string;
	value?: SingleCheckboxValue;
	item?: CheckboxItemInfo;
	scrollableLabel?: boolean;
	isDisabled?: boolean;
	isGroupDisabled?: boolean;
	onBeforeChange?: (changingTo: boolean) => Promise<unknown>; // supporting only for single checkboxes, not groups
	theme?: string | null;
	size?: string;
}

interface CheckboxArgsSingle extends CheckboxArgsBase {
	selected: boolean | null | undefined;
	onChange: (newSelected: boolean) => void;
}

interface CheckboxArgsGroup extends CheckboxArgsBase {
	selected: GroupSelected;
	onChange: (newSelected: GroupSelected) => void;
}

export type Args = CheckboxArgsSingle | CheckboxArgsGroup;

@themed()
export default class FormCheckboxComponent extends Component<Args> {
	guid = guidFor(this);

	declare themeClass: string; // from @themed
	declare sizeClass: string; // from @themed

	get id() {
		return `${this.args.name || 'checkbox'}-${this.guid}`;
	}

	get isDisabled() {
		return this.args.isDisabled || this.args.isGroupDisabled;
	}

	get hasTooltip() {
		return this.args.item?.tooltip;
	}

	get hasLinkOrTooltip() {
		return this.hasTooltip || this.args.item?.link;
	}

	get iconName(): IconName {
		return this.args.item?.tooltipIcon || 'Question';
	}

	get isPartOfCheckboxGroup() {
		const args = this.args;
		return 'selected' in args && Array.isArray(args.selected);
	}

	get selectedForCheckboxSingle() {
		const args = this.args as CheckboxArgsSingle;
		return args.selected;
	}

	get onChangeForCheckboxSingle() {
		const args = this.args as CheckboxArgsSingle;
		return args.onChange;
	}

	get selectedForCheckboxGroup() {
		const args = this.args as CheckboxArgsGroup;
		return args.selected;
	}

	get onChangeForCheckboxGroup() {
		const args = this.args as CheckboxArgsGroup;
		return args.onChange;
	}

	get isChecked() {
		if (this.isPartOfCheckboxGroup) {
			const args = this.args as CheckboxArgsGroup;
			return args.selected.includes(args.value!);
		} else {
			const args = this.args as CheckboxArgsSingle;
			return !!args.selected;
		}
	}

	@action
	doLinkAction(e: PointerEvent) {
		this.args.item?.link?.action();
		e.preventDefault();
	}

	// private isPartOfCheckboxGroup(args: Args): args is CheckboxArgsGroup {
	// 	return 'selected' in args && Array.isArray(args.selected);
	// }
	<template>
		<span
			class={{joinClasses
				(array
					"checkbox"
					this.themeClass
					this.sizeClass
					(if this.hasTooltip "hasTooltip")
					(if this.isDisabled "disabled")
					(if this.hasLinkOrTooltip "hasLinkOrTooltip")
				)
			}}
		>

			{{#if this.isPartOfCheckboxGroup}}
				<FormCheckboxSingleComponent
					@id={{this.id}}
					@name={{@name}}
					@value={{@value}}
					@selected={{this.selectedForCheckboxGroup}}
					@isDisabled={{this.isDisabled}}
					@onBeforeChange={{@onBeforeChange}}
					@onChange={{this.onChangeForCheckboxGroup}}
				/>
			{{else}}
				<FormCheckboxSingleComponent
					@id={{this.id}}
					@name={{@name}}
					@value={{@value}}
					@selected={{this.selectedForCheckboxSingle}}
					@isDisabled={{this.isDisabled}}
					@onBeforeChange={{@onBeforeChange}}
					@onChange={{this.onChangeForCheckboxSingle}}
				/>
			{{/if}}

			<label for={{this.id}}>
				{{#if this.isChecked}}
					<CommonUiIconComponent
						@name="CheckboxTick"
						@size="xs"
						@class="checkbox-check"
					/>
				{{/if}}
				{{#if @scrollableLabel}}
					<ScrollableTextComponent>
						{{{@label}}}
					</ScrollableTextComponent>
				{{else}}
					{{{@label}}}
				{{/if}}

				{{#if @item.tooltip}}
					<ToolTipWithIconComponent
						@text={{@item.tooltip}}
						@iconName={{this.iconName}}
						@maxWidth={{@item.tooltipMaxWidth}}
						@theme={{@item.tooltipTheme}}
					/>
				{{/if}}
			</label>

			{{#if @item.used}}
				<span class="used">{{@item.used}}</span>
				{{#if @item.usedTooltip}}
					<ToolTipComponent @text={{@item.usedTooltip}} />
				{{/if}}
			{{/if}}

			{{#if @item.link.href}}
				<a
					href={{@item.link.href}}
					style={{@item.link.style}}
					target={{@item.link.target}}
				>{{@item.link.label}}
				</a>
			{{else if @item.link.action}}
				<a
					role="button"
					class="link"
					{{on "click" this.doLinkAction}}
					style={{@item.link.style}}
				>{{@item.link.label}}
				</a>
			{{/if}}

		</span>
	</template>
}
